import Destination from '@/components/Passenger/models/Destination';
import PassengerDestination from '@/components/Passenger/models/PassengerDestination';
import restService from '@/services/restService';
import {
  IEntity,
  IEntityImporter,
  IEntityMapping,
  IExcelRow,
  IPassengerMatchRequest,
  IPropertyUpdate
} from '@/typings/import';
import Passenger from '@/components/Passenger/models/Passenger';
import { EEntityMatchType } from '@/components/Importer/enums';
import PassengerMapping from './PassengerMapping';
import PassengerEntity from './PassengerEntity';
import PropertyUpdateHelper from '../PropertyUpdateHelper';
import PassengerSearchMapping from './PassengerSearchMapping';
// import store from '@/store/OrganizationStore';

export interface IPassengerImporterParams {
  destinations: Destination[];
}

export class PassengerImporter implements IEntityImporter<Passenger> {
  data: IExcelRow[];
  mapping: IEntityMapping<Passenger>;
  entities: Array<IEntity<Passenger>>;

  constructor(data: IExcelRow[], mapping: IEntityMapping<Passenger>) {
    this.data = data;
    this.mapping = mapping;
    this.entities = this.data.map((d, i) => new PassengerEntity(d, i, this.mapping));
  }

  async matchExisting(row: number, params: IPassengerImporterParams): Promise<Passenger> {
    const entities = this.entities.filter(e => e.excelRowIndex === row);

    let updatedEntities = [];
    for (const entity of entities) {
      const externalId = this.getNewValueString(entity, 'externalId');
      const ssn = this.getNewValueString(entity, 'ssn');
      const name = this.getNewValueString(entity, 'name');

      const request: IPassengerMatchRequest = { externalId, ssn, name };
      const result = await restService.passengers.matchPassengers(request);

      const foundEntities = result.map(r => {
        const entityResult = new PassengerEntity(this.data[row], row, this.mapping);
        entityResult.entity = new Passenger(r.result);
        entityResult.matchedBy = r.matchedBy;
        entityResult.propertyUpdates.forEach(pu => {
          pu.oldValue = entityResult.entity[pu.propertyName] || null;
        });

        const utilitiesProp = entityResult.propertyUpdates
          .find(pu => pu.propertyName === PassengerMapping.PROPERTY_MAPPING_UTILITIES);

        if (utilitiesProp) {
          const mappingPropertyValues = this.mapping.props.filter(mp => {
            if (!mp.propertyName.includes(PassengerMapping.UTILITY_KEY)) {
              return false;
            }
            const nameParts = mp.propertyName.split('-');
            if (nameParts.length !== 2) {
              return false;
            }
            return PropertyUpdateHelper.propertyMappingIsDefined(mp);
          })
            .map(mp => mp.propertyName.split('-')[1]);

          const undefinedPassengerUtilities = entityResult.entity.utilities.split(',')
            .filter(puv => !mappingPropertyValues.includes(puv));

          let updatedUtilityValues = PropertyUpdateHelper.getNewValueAsPropertyValue<string>(utilitiesProp).split(',');
          updatedUtilityValues = updatedUtilityValues.concat(undefinedPassengerUtilities)
            .filter((c, i, a) => a.indexOf(c) === i);

          utilitiesProp.newValue = updatedUtilityValues.join(',') as any;
        }

        return entityResult;
      }).filter(e => {
        // Check that found match has primary and secondary destination in the correct movit instance
        // remove from matches if not

        let destinationMatch = true;

        const destProp = this.getDestinationPropertyUpdate(e);

        if (this.shouldUpdateProperty(destProp, e.matchedBy)) {
          destinationMatch = this
          .matchDestinationInSameMovitInstance(destProp, params.destinations, e.entity.movitInstanceId);
        }
        
        let secondaryDestinationMatch = true;
        if (e.entity.secondaryDestinationId) {

            const secondaryDestProp = this.getSecondaryDestinationPropertyUpdate(e);

            if (secondaryDestProp && this.shouldUpdateProperty(secondaryDestProp, e.matchedBy)) {
              secondaryDestinationMatch = this
              .matchDestinationInSameMovitInstance(secondaryDestProp, params.destinations, e.entity.movitInstanceId);
            }
        }
        return destinationMatch && secondaryDestinationMatch;
      });

      if (foundEntities.length === 0) {
        const newPassenger = new Passenger(null);
        entity.propertyUpdates
          .filter(pu => pu.propertyName !== PassengerMapping.PROPERTY_MAPPING_SSN)
          .forEach(pu => (newPassenger[pu.propertyName] = pu.newValue));

        entity.entity = newPassenger;
        entity.matchedBy = EEntityMatchType.Created;
      } else {
        const first = foundEntities.shift();
        entity.entity = first.entity;
        entity.matchedBy = first.matchedBy;
        entity.propertyUpdates = first.propertyUpdates;
      }
      
      updatedEntities = updatedEntities.concat(foundEntities);
    }

    this.entities = this.entities.concat(updatedEntities);
    return null; // TODO: Why should we return something?
  }

  async postEntity(entity: IEntity<Passenger>, params: IPassengerImporterParams): Promise<Passenger> {
    const passenger = entity.entity;
    if (!passenger) {
      return null;
    }

    const doNotUpdateProperties = [
      PassengerMapping.PROPERTY_MAPPING_DESTINATION,
      PassengerMapping.PROPERTY_MAPPING_SECONDARY_DESTINATION,
      PassengerMapping.PROPERTY_MAPPING_SSN,
    ];

    entity.propertyUpdates
    .filter(pu => !pu.doNotUpdate && !doNotUpdateProperties.includes(pu.propertyName))
    .filter(pu => this.shouldUpdateProperty(pu, entity.matchedBy))
    .forEach(pu => passenger[pu.propertyName] = PropertyUpdateHelper.getValue(pu));

    const destinationProperty = this.getDestinationPropertyUpdate(entity);

    if (destinationProperty && this.shouldUpdateProperty(destinationProperty, entity.matchedBy)) {
      const passengerDestination = this.getPassengerDestination(entity, params.destinations);
      if (passengerDestination === null) {
        return null;
      }
      
      passenger.passengerDestinations = [ passengerDestination ];
    }

    const secondaryDestProp = this.getSecondaryDestinationPropertyUpdate(entity);
    const secondaryDestValue = secondaryDestProp ? PropertyUpdateHelper.getValue(secondaryDestProp) : null;

    if (secondaryDestValue && this.shouldUpdateProperty(secondaryDestProp, entity.matchedBy)) {
      const destination = this.getDestination(secondaryDestProp, params.destinations);
      if (destination === null) {
        return null;
      }
      passenger.secondaryDestinationId = destination.id;
    }
    const importedPassenger = await this.createOrUpdatePassenger(passenger);

    const ssn = this.getSsnValue(entity);
    if (ssn && entity.matchedBy !== EEntityMatchType.Ssn) {
      const importedSsn = await restService.passengers.setPassengerSsn(importedPassenger.id, ssn);
      if (importedSsn) {
        importedPassenger.hasSsn = true;
      }
    }

    return importedPassenger;
  }

  onMappingChanged(mapping: IEntityMapping<Passenger>) {
    this.mapping = mapping;
    this.entities.forEach(e => e.onMappingChanged(mapping, this.data[e.excelRowIndex])
    );
  }

  private getNewValueString(entity: IEntity<Passenger>, propertyName: string): string {
    const property = entity.propertyUpdates.find(pu => pu.propertyName === propertyName);
    if (!property) {
      return '';
    }

    const newValue = property.newValue || null;
    if (newValue === null) {
      return '';
    }

    return (newValue as unknown) as string;
  }

  private getPassengerDestination(
    entity: IEntity<Passenger>,
    destinations: Destination[]
  ): PassengerDestination | null {
    const destProp = this.getDestinationPropertyUpdate(entity);

    const destination = this.getDestination(destProp, destinations);
    if (!destination)  {
      return null;
    }

    const passengerDestination = new PassengerDestination(0, destination.id);

    return passengerDestination;
  }

  private getDestination(propertyUpdate: IPropertyUpdate<any>, destinations: Destination[]) {
    const requiredPropertyNames = [
      PassengerMapping.PROPERTY_MAPPING_DESTINATION,
      PassengerMapping.PROPERTY_MAPPING_SECONDARY_DESTINATION,
    ];

    if (!propertyUpdate || !requiredPropertyNames.includes(propertyUpdate.propertyName)) {
      return null;
    }

    const destinationAlias = PropertyUpdateHelper.getValue(propertyUpdate);
    
    const destination = destinations.find(d => d.alias === destinationAlias);
    if (!destination) {
      return null;
    }
    return destination;
  }

  private matchDestinationInSameMovitInstance(
    propertyUpdate: IPropertyUpdate<any>, 
    destinations: Destination[], 
    movitInstanceId: number
  ) {
    const destination = this.getDestination(propertyUpdate, destinations);
    if (!destination) {
      return false;
    }

    return destination.movitInstanceId === movitInstanceId;
  }

  private shouldUpdateProperty(propertyUpdate: IPropertyUpdate<any>, matchedBy: EEntityMatchType) {
    if (matchedBy === EEntityMatchType.Created) {
      return true;
    }
    // Keep matched original values if no mapping has been given
    const mappingProperty = this.mapping.props.find(p => p.propertyName === propertyUpdate.propertyName);
    if (!mappingProperty) {
      return false;
    }
    return PropertyUpdateHelper.propertyMappingIsDefined(mappingProperty);
  }

  private getDestinationPropertyUpdate(entity: IEntity<Passenger>) {
    const destinationProperty = entity.propertyUpdates
    .find(pu => pu.propertyName === PassengerMapping.PROPERTY_MAPPING_DESTINATION);

    return destinationProperty || null;
  }

  private getSecondaryDestinationPropertyUpdate(entity: IEntity<Passenger>) {
    const secondaryDestination = entity.propertyUpdates
    .find(pu => pu.propertyName === PassengerMapping.PROPERTY_MAPPING_SECONDARY_DESTINATION);

    return secondaryDestination || null;
  }

  private async createOrUpdatePassenger(passenger: Passenger): Promise<Passenger> {
    if (passenger.id === 0) {
      const createdPassenger = await restService.passengers.createPassenger(passenger);
      return new Passenger(createdPassenger);
    }

    const updatedPassenger = await restService.passengers.updatePassenger(passenger);
    return new Passenger(updatedPassenger);
  }

  private getSsnValue(entity: IEntity<Passenger>) {
    const ssnPropertyUpdate = entity.propertyUpdates
      .find(pu => pu.propertyName === PassengerMapping.PROPERTY_MAPPING_SSN);
    
    if (!ssnPropertyUpdate) {
      return null;
    }

    if (!this.shouldUpdateProperty(ssnPropertyUpdate, entity.matchedBy)) {
      return null;
    }

    return PropertyUpdateHelper.getValue(ssnPropertyUpdate);
  }
}
